import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import { Wrench } from 'lucide-react';
import { theme } from '@styles/theme';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { useFeatureFlagsWithSetter } from '@state/index';
const SEED_USERS = [
    {
        email: 'admin@shift.com',
        password: 'AdminPassword123!',
        name: 'Admin User',
        role: 'admin',
    },
    {
        email: 'client@example.com',
        password: 'ClientPassword123!',
        name: 'Test Client',
        role: 'client',
    },
];
// Add labels for future feature flags here
const FEATURE_FLAG_LABELS = {
// Example: newFeatureName: 'New Feature Name',
};
const DevToolsButton = styled.button `
  position: fixed;
  bottom: ${theme.spacing.md};
  left: ${theme.spacing.md};
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};
  z-index: 100;

  &:hover {
    background-color: ${theme.colors.border.secondary};
    color: ${theme.colors.text.primary};
    transform: scale(1.05);
  }
`;
const Section = styled.div `
  margin-bottom: ${theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;
const SectionTitle = styled.h3 `
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${theme.spacing.sm} 0;
`;
const UserList = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;
const UserButton = styled.button `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.border.secondary};
    border-color: ${theme.colors.border.secondary};
  }
`;
const UserInfo = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const UserName = styled.span `
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
`;
const UserEmail = styled.span `
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;
const RoleBadge = styled.span `
  font-size: ${theme.typography.fontSize.xs};
  padding: 2px ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${({ $role }) => $role === 'admin' ? theme.colors.status.warning + '20' : theme.colors.status.info + '20'};
  color: ${({ $role }) => ($role === 'admin' ? theme.colors.status.warning : theme.colors.status.info)};
  text-transform: capitalize;
`;
const FlagList = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;
const FlagRow = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
`;
const FlagLabel = styled.span `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
`;
const Toggle = styled.button `
  width: 44px;
  height: 24px;
  border-radius: ${theme.borderRadius.full};
  border: none;
  cursor: pointer;
  position: relative;
  transition: all ${theme.transitions.fast};
  background-color: ${({ $enabled }) => ($enabled ? theme.colors.status.success : theme.colors.border.secondary)};

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ $enabled }) => ($enabled ? '22px' : '2px')};
    width: 20px;
    height: 20px;
    border-radius: ${theme.borderRadius.full};
    background-color: ${theme.colors.text.primary};
    transition: all ${theme.transitions.fast};
  }
`;
const EmptyState = styled.div `
  padding: ${theme.spacing.md};
  text-align: center;
  color: ${theme.colors.text.secondary};
  font-style: italic;
  background-color: ${theme.colors.background.tertiary};
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
`;
const DevBadge = styled.div `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: 2px ${theme.spacing.sm};
  background-color: ${theme.colors.status.warning}20;
  color: ${theme.colors.status.warning};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing.md};
`;
function isLocalEnvironment() {
    return process.env.NODE_ENV === 'development';
}
export function DevTools({ onFillCredentials }) {
    const [isOpen, setIsOpen] = useState(false);
    const { flags, setFlag } = useFeatureFlagsWithSetter();
    if (!isLocalEnvironment()) {
        return null;
    }
    const handleUserClick = (email, password) => {
        onFillCredentials(email, password);
        setIsOpen(false);
    };
    const handleToggleFlag = (key) => {
        setFlag(key, !flags[key]);
    };
    const flagKeys = Object.keys(flags);
    return (_jsxs(_Fragment, { children: [_jsx(DevToolsButton, { onClick: () => setIsOpen(true), title: "Developer Tools", "aria-label": "Open developer tools", children: _jsx(Wrench, { size: 20 }) }), _jsxs(Modal, { isOpen: isOpen, onClose: () => setIsOpen(false), title: "Developer Tools", size: "sm", children: [_jsx(DevBadge, { children: "Local Development Only" }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Quick Login" }), _jsx(UserList, { children: SEED_USERS.map((user) => (_jsxs(UserButton, { onClick: () => handleUserClick(user.email, user.password), children: [_jsxs(UserInfo, { children: [_jsx(UserName, { children: user.name }), _jsx(UserEmail, { children: user.email })] }), _jsx(RoleBadge, { "$role": user.role, children: user.role })] }, user.email))) })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Feature Flags" }), flagKeys.length === 0 ? (_jsx(EmptyState, { children: "No feature flags currently defined" })) : (_jsx(FlagList, { children: flagKeys.map((key) => (_jsxs(FlagRow, { children: [_jsx(FlagLabel, { children: FEATURE_FLAG_LABELS[key] }), _jsx(Toggle, { "$enabled": flags[key], onClick: () => handleToggleFlag(key), "aria-label": `Toggle ${FEATURE_FLAG_LABELS[key]}`, role: "switch", "aria-checked": flags[key] })] }, key))) }))] }), _jsx(Button, { variant: "secondary", fullWidth: true, onClick: () => setIsOpen(false), style: { marginTop: theme.spacing.md }, children: "Close" })] })] }));
}
